/**
 * Reisegruppen-Widget T1 / Punkt 129 — gemeinsame Regeln (Entscheidung E-W22).
 *
 * EIN Modul fuer API (Pruefung), Loader (Anzeige) und Editor (Vorschau), damit „welches Feld ist
 * gerade sichtbar“ und „welcher Monat ist waehlbar“ nirgends zweimal geschrieben steht. Bewusst
 * klein, zod-frei, ohne DOM und ohne Laufzeit-Abhaengigkeit, ES2019-tauglich (Loader-Bundle
 * laeuft auf fremden Websites unter der 30-kB-Grenze). Import: `@maestro/widgets/regeln`.
 *
 * Alle Funktionen sind rein. „Heute“ kommt immer als Text `YYYY-MM-DD` (oder `YYYY-MM`) von
 * aussen: der Server rechnet in Berliner Zeit (`berlinHeute`), der Browser in seiner eigenen.
 */

/** „Nur zeigen, wenn Feld `feld` einen dieser `werte` hat.“ */
export interface Bedingung {
  feld: string;
  werte: readonly string[];
}

/** Zielfeld -> Bedingung. Genau eine Bedingung je Zielfeld. */
export type Bedingungen = Readonly<Record<string, Bedingung>>;

export type Werte = Readonly<Record<string, unknown>>;

const HAT = Object.prototype.hasOwnProperty;

function bedingungVon(bedingungen: Bedingungen, schluessel: string): Bedingung | null {
  return (HAT.call(bedingungen, schluessel) && bedingungen[schluessel]) || null;
}

/** Wert eines Feldes als Vergleichstext: Haekchen `true`/`false`, Zahlen als Ziffern, sonst Text. */
export function wertAlsText(wert: unknown): string {
  if (typeof wert === "string") return wert.trim();
  if (typeof wert === "boolean" || typeof wert === "number") return String(wert);
  return "";
}

/**
 * Welche Felder sind bei diesen Werten sichtbar? Ein Feld ohne Bedingung ist immer sichtbar.
 * Ein Feld mit Bedingung ist sichtbar, wenn seine Quelle SELBST sichtbar ist und ihr Wert zu den
 * geforderten gehoert — ein unsichtbares Quellfeld macht sein Ziel also mit unsichtbar (Ketten).
 * Die Auswertung geht rekursiv zur Quelle und ist damit topologisch; ein Kreis (den die Pruefung
 * beim Speichern verbietet) gilt vorsichtshalber als unsichtbar, statt endlos zu laufen.
 *
 * `schluessel`: die zu bewertenden Felder. Ohne Angabe alle, die in den Bedingungen vorkommen
 * (Ziele und Quellen). Ergebnis: die Menge der sichtbaren Schluessel daraus.
 */
export function sichtbareSchluessel(bedingungen: Bedingungen, werte: Werte, schluessel?: Iterable<string>): Set<string> {
  const alle: string[] = [];
  if (schluessel) {
    for (const k of schluessel) alle.push(k);
  } else {
    for (const ziel of Object.keys(bedingungen)) {
      const b = bedingungVon(bedingungen, ziel);
      alle.push(ziel);
      if (b) alle.push(b.feld);
    }
  }
  const stand: Record<string, 0 | 1 | 2 | 3> = {}; // 0 offen, 1 in Arbeit, 2 sichtbar, 3 unsichtbar
  const pruefe = (k: string): boolean => {
    const s = HAT.call(stand, k) ? stand[k] : 0;
    if (s === 2) return true;
    if (s === 3 || s === 1) return false;
    const b = bedingungVon(bedingungen, k);
    if (!b) {
      stand[k] = 2;
      return true;
    }
    stand[k] = 1;
    const ok = pruefe(b.feld) && b.werte.indexOf(wertAlsText(HAT.call(werte, b.feld) ? werte[b.feld] : undefined)) >= 0;
    stand[k] = ok ? 2 : 3;
    return ok;
  };
  const sichtbar = new Set<string>();
  for (const k of alle) if (pruefe(k)) sichtbar.add(k);
  return sichtbar;
}

/**
 * Ein Kreis in den Bedingungen als Pfad `[A, B, A]` (erstes = letztes Element), sonst `null`.
 * Kante: Zielfeld haengt von seinem Quellfeld ab.
 */
export function findeKreis(bedingungen: Bedingungen): string[] | null {
  const farbe: Record<string, 1 | 2> = {}; // 1 auf dem Pfad, 2 fertig
  const pfad: string[] = [];
  const besuche = (k: string): string[] | null => {
    if (HAT.call(farbe, k)) {
      if (farbe[k] === 1) return pfad.slice(pfad.indexOf(k)).concat(k);
      return null;
    }
    farbe[k] = 1;
    pfad.push(k);
    const b = bedingungVon(bedingungen, k);
    if (b) {
      const kreis = besuche(b.feld);
      if (kreis) return kreis;
    }
    pfad.pop();
    farbe[k] = 2;
    return null;
  };
  for (const ziel of Object.keys(bedingungen)) {
    const kreis = besuche(ziel);
    if (kreis) return kreis;
  }
  return null;
}

/* ------------------------------------------------------------------------------------------ *
 * Zeit: Monate und Tage
 * ------------------------------------------------------------------------------------------ */

const MONAT_MUSTER = /^(\d{4})-(0[1-9]|1[0-2])$/;
const TAG_MUSTER = /^(\d{4})-(0[1-9]|1[0-2])-(\d{2})$/;

function zweistellig(n: number): string {
  return n < 10 ? "0" + n : String(n);
}

/** Monat als fortlaufende Zahl (Jahr * 12 + Monat - 1); `null` bei ungueltigem `YYYY-MM`. */
function monatsIndex(monat: string): number | null {
  const m = MONAT_MUSTER.exec(monat);
  return m ? Number(m[1]) * 12 + Number(m[2]) - 1 : null;
}

function ausIndex(i: number): string {
  return String(Math.floor(i / 12)).padStart(4, "0") + "-" + zweistellig((i % 12) + 1);
}

/** Der Monat (`YYYY-MM`) eines Tages- oder Monatstextes; `null`, wenn er nicht lesbar ist. */
function monatVon(heute: string): string | null {
  const t = TAG_MUSTER.exec(heute);
  if (t) return t[1] + "-" + t[2];
  return MONAT_MUSTER.test(heute) ? heute : null;
}

/**
 * Berliner Kalendertag (`YYYY-MM-DD`) eines Zeitpunkts. Der Server rechnet immer so: ein Gast
 * um 00:30 in Berlin bekommt nicht den Vortag, nur weil der Worker in UTC laeuft.
 */
export function berlinHeute(jetzt: Date): string {
  const teile = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Berlin",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(jetzt);
  const nimm = (t: string): string => (teile.find((p) => p.type === t) || { value: "" }).value;
  return nimm("year") + "-" + nimm("month") + "-" + nimm("day");
}

/**
 * Die naechsten `n` Monate ab dem Monat von `heute` (dieser Monat eingeschlossen) als `YYYY-MM`,
 * immer mit VIERSTELLIGEM Jahr, ueber den Jahreswechsel hinweg. Ungueltiges `heute` -> leere Liste.
 */
export function monatsListe(heute: string, n: number): string[] {
  const start = monatVon(heute);
  const i0 = start === null ? null : monatsIndex(start);
  if (i0 === null || !(n > 0)) return [];
  const ergebnis: string[] = [];
  for (let i = 0; i < n; i++) ergebnis.push(ausIndex(i0 + i));
  return ergebnis;
}

/** Der Monat vor dem aktuellen (Kulanz: Monatswechsel, Zeitzonen). Ungueltig -> `null`. */
export const ZEITRAUM_MONATE_ZURUECK = 1;
/** Hoechstens so viele Monate ab dem aktuellen sind planbar. */
export const ZEITRAUM_MONATE_VOR = 24;

export type ZeitraumErgebnis =
  | { ok: true; offen: true; monat: null; tag: null }
  | { ok: true; offen: false; monat: string; tag: string | null }
  | { ok: false; grund: "zeitraum" | "fenster" | "tag_monat" | "tag_vergangen" | "offen_nicht_erlaubt"; nachricht: string };

/** Ist `YYYY-MM-DD` ein echter Kalendertag? */
function istKalendertag(tag: string): boolean {
  const t = TAG_MUSTER.exec(tag);
  if (!t) return false;
  const d = new Date(Date.UTC(Number(t[1]), Number(t[2]) - 1, Number(t[3])));
  return d.getUTCFullYear() === Number(t[1]) && d.getUTCMonth() === Number(t[2]) - 1 && d.getUTCDate() === Number(t[3]);
}

/**
 * Zeitraum einer Anfrage pruefen: `offen` (nur wenn `offenErlaubt`) oder ein Monat `YYYY-MM` vom
 * Vormonat bis +24 Monate ab `heute`. Ein `tag` ist freiwillig, muss ein echter Kalendertag im
 * gewaehlten Monat sein und darf nicht vor `heute` liegen. Bei `offen` wird ein `tag` ignoriert.
 */
export function pruefeZeitraum(zeitraum: unknown, tag: unknown, heute: string, offenErlaubt: boolean = true): ZeitraumErgebnis {
  const jetzt = monatVon(heute);
  const i0 = jetzt === null ? null : monatsIndex(jetzt);
  const fehler = (grund: "zeitraum" | "fenster" | "tag_monat" | "tag_vergangen" | "offen_nicht_erlaubt", nachricht: string): ZeitraumErgebnis => ({ ok: false, grund, nachricht });
  if (i0 === null) return fehler("zeitraum", "Das heutige Datum ist nicht lesbar.");
  const text = typeof zeitraum === "string" ? zeitraum.trim() : "";
  if (text === "offen") {
    if (!offenErlaubt) return fehler("offen_nicht_erlaubt", "„Termin noch offen“ ist bei diesem Formular nicht vorgesehen. Bitte einen Monat wählen.");
    return { ok: true, offen: true, monat: null, tag: null };
  }
  const i = monatsIndex(text);
  if (i === null) return fehler("zeitraum", "Bitte einen Monat im Format JJJJ-MM wählen, zum Beispiel 2027-05.");
  if (i < i0 - ZEITRAUM_MONATE_ZURUECK || i > i0 + ZEITRAUM_MONATE_VOR) {
    return fehler("fenster", "Dieser Monat liegt außerhalb des Zeitraums, den wir planen können. Bitte einen Monat in den nächsten zwei Jahren wählen.");
  }
  if (tag === undefined || tag === null || (typeof tag === "string" && tag.trim() === "")) {
    return { ok: true, offen: false, monat: text, tag: null };
  }
  const tagText = typeof tag === "string" ? tag.trim() : "";
  if (!istKalendertag(tagText) || tagText.slice(0, 7) !== text) {
    return fehler("tag_monat", "Der Wunschtag muss ein Tag im gewählten Monat sein.");
  }
  const heuteTag = TAG_MUSTER.test(heute) ? heute : null;
  if (heuteTag !== null && tagText < heuteTag) {
    return fehler("tag_vergangen", "Der Wunschtag darf nicht in der Vergangenheit liegen.");
  }
  return { ok: true, offen: false, monat: text, tag: tagText };
}

/* ------------------------------------------------------------------------------------------ *
 * Einheit E2a: Wunschtag und Ausweichtermin (Kalender statt Monatswahl)
 * ------------------------------------------------------------------------------------------ */

export type TagGrund =
  | "wunsch_fehlt"
  | "wunsch_ungueltig"
  | "wunsch_vergangen"
  | "wunsch_fenster"
  | "wunsch_gesperrt"
  | "ausweich_ungueltig"
  | "ausweich_vergangen"
  | "ausweich_fenster"
  | "ausweich_gesperrt"
  | "ausweich_gleich";

export type TagErgebnis = { ok: true; wunsch: string; ausweich: string | null } | { ok: false; grund: TagGrund; nachricht: string };

const MONATSNAMEN_DE = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"];

/** „2026-12-11“ als „11. Dezember 2026“ (feste deutsche Schreibweise fuer Klartexte des Servers, ohne Intl). */
function deutschesDatum(tag: string): string {
  const t = TAG_MUSTER.exec(tag);
  return t ? Number(t[3]) + ". " + MONATSNAMEN_DE[Number(t[2]) - 1] + " " + t[1] : tag;
}

/**
 * Wunschtag (Pflicht) und optionaler Ausweichtermin einer Anfrage pruefen. Beide sind echte Kalendertage
 * `YYYY-MM-DD`, nicht vor `heute` (Berliner Kalendertag), hoechstens bis zum Ende des Monats +24 ab dem
 * laufenden, und kein Sperrtag (`sperrtage`: Schliesstage des Betriebs, `YYYY-MM-DD`). Der Ausweichtermin
 * muss ein anderer Tag sein als der Wunschtag; leer/`null` heisst „kein Ausweichtermin“.
 */
export function pruefeTag(wunsch: unknown, ausweich: unknown, heute: string, sperrtage: readonly string[] = []): TagErgebnis {
  const jetzt = monatVon(heute);
  const i0 = jetzt === null ? null : monatsIndex(jetzt);
  const heuteTag = TAG_MUSTER.test(heute) ? heute : null;
  const fehler = (grund: TagGrund, nachricht: string): TagErgebnis => ({ ok: false, grund, nachricht });
  if (i0 === null || heuteTag === null) return fehler("wunsch_ungueltig", "Das heutige Datum ist nicht lesbar.");
  const leer = (v: unknown): boolean => v === undefined || v === null || (typeof v === "string" && v.trim() === "");
  const pruefe = (wert: unknown, art: "wunsch" | "ausweich"): TagErgebnis => {
    const name = art === "wunsch" ? "Der Wunschtag" : "Der Ausweichtermin";
    const text = typeof wert === "string" ? wert.trim() : "";
    if (!istKalendertag(text)) return fehler(art === "wunsch" ? "wunsch_ungueltig" : "ausweich_ungueltig", name + " muss ein Datum im Format JJJJ-MM-TT sein, zum Beispiel 2026-12-11.");
    if (text < heuteTag) return fehler(art === "wunsch" ? "wunsch_vergangen" : "ausweich_vergangen", name + " darf nicht in der Vergangenheit liegen. Bitte einen Tag ab heute wählen.");
    const i = monatsIndex(text.slice(0, 7));
    if (i === null || i > i0 + ZEITRAUM_MONATE_VOR) {
      return fehler(art === "wunsch" ? "wunsch_fenster" : "ausweich_fenster", name + " liegt außerhalb des Zeitraums, den wir planen können. Bitte einen Tag in den nächsten zwei Jahren wählen.");
    }
    if (sperrtage.indexOf(text) >= 0) {
      return fehler(art === "wunsch" ? "wunsch_gesperrt" : "ausweich_gesperrt", "Am " + deutschesDatum(text) + " ist unser Haus geschlossen. Bitte wählen Sie einen anderen Tag.");
    }
    return { ok: true, wunsch: text, ausweich: null };
  };
  if (leer(wunsch)) return fehler("wunsch_fehlt", "Bitte wählen Sie einen Tag für Ihre Feier.");
  const w = pruefe(wunsch, "wunsch");
  if (!w.ok) return w;
  if (leer(ausweich)) return w;
  const a = pruefe(ausweich, "ausweich");
  if (!a.ok) return a;
  if (a.wunsch === w.wunsch) return fehler("ausweich_gleich", "Der Ausweichtermin muss ein anderer Tag sein als Ihr Wunschtag.");
  return { ok: true, wunsch: w.wunsch, ausweich: a.wunsch };
}

/**
 * Der Monat (`YYYY-MM`), in dem der Kalender oeffnet: `monat` (1 bis 12) im laufenden Jahr, nach dem
 * Monat im naechsten Jahr (der laufende Monat zaehlt noch). Ohne Angabe nach dem Anlass
 * („Weihnachtsfeier“ Dezember, „Neujahrsempfang“ Januar), sonst der naechste Monat.
 */
export function kalenderStartMonat(heute: string, monat?: number | null, anlass?: string | null): string {
  const jetzt = monatVon(heute) || "2000-01";
  const jahr = Number(jetzt.slice(0, 4));
  const laufend = Number(jetzt.slice(5, 7));
  let m = typeof monat === "number" && Number.isInteger(monat) && monat >= 1 && monat <= 12 ? monat : 0;
  if (m === 0 && anlass) m = /weihnacht/i.test(anlass) ? 12 : /neujahr/i.test(anlass) ? 1 : 0;
  if (m === 0) return ausIndex((monatsIndex(jetzt) as number) + 1);
  return String(m >= laufend ? jahr : jahr + 1).padStart(4, "0") + "-" + zweistellig(m);
}

/* ------------------------------------------------------------------------------------------ *
 * Gruppengroesse
 * ------------------------------------------------------------------------------------------ */

/**
 * Zu welchem Bereich gehoert eine Personenzahl? Ergebnis ist die UNTERGRENZE des Bereichs (die
 * groesste Untergrenze, die nicht ueber `wert` liegt) oder `null`, wenn `wert` unter der kleinsten
 * liegt (= unter der Mindestgruppe) oder keine ganze Zahl ist.
 */
export function bereichAus(untergrenzen: readonly number[], wert: number): number | null {
  if (typeof wert !== "number" || !isFinite(wert) || Math.floor(wert) !== wert) return null;
  let treffer: number | null = null;
  for (const u of untergrenzen) {
    if (u <= wert && (treffer === null || u > treffer)) treffer = u;
  }
  return treffer;
}

/** Obergrenze einer Zahl ueberhaupt (wie `guests`). */
export const GAESTE_MAXIMUM = 100000;

/** Grenzen eines Bereichs: von der Untergrenze bis zur naechsten minus 1, der letzte bis zum Maximum. */
export function bereichGrenzen(untergrenzen: readonly number[], untergrenze: number): { von: number; bis: number } | null {
  const sortiert = untergrenzen.slice().sort((a, b) => a - b);
  const i = sortiert.indexOf(untergrenze);
  if (i < 0) return null;
  return { von: untergrenze, bis: i + 1 < sortiert.length ? (sortiert[i + 1] as number) - 1 : GAESTE_MAXIMUM };
}

/** Passt ein Menue mit Gaeste-Grenzen (null = offen) zur Spanne von..bis? Ohne Grenzen immer. */
export function menuePasst(min: number | null | undefined, max: number | null | undefined, von: number, bis: number): boolean {
  return (typeof min !== "number" || min <= bis) && (typeof max !== "number" || max >= von);
}

/**
 * Ablagetext eines Bereichs: „40–59“ (mit Halbgeviertstrich), der letzte als „über 100“
 * (Untergrenze 101). Wird in `inquiries.details.gaesteBereich` gespeichert und angezeigt.
 */
export function bereichText(untergrenzen: readonly number[], untergrenze: number): string | null {
  const g = bereichGrenzen(untergrenzen, untergrenze);
  if (!g) return null;
  const sortiert = untergrenzen.slice().sort((a, b) => a - b);
  return untergrenze === sortiert[sortiert.length - 1] ? "über " + (untergrenze - 1) : g.von + "–" + g.bis;
}
